import {analysisAreaTypes, analysisCategories, AnalysisInfo} from '@domain/rcfa'
import {StoppageSection, StoppageInfo} from '@domain/stoppages'
import {AnalysisType} from '@hconnect/common/types'
import {Button, ControlledSelect, Icon} from '@hm/fundamental'
import {mdiArrowLeft, mdiArrowRight, mdiLoading} from '@mdi/js'
import {TextField, Box, Typography, Stack, Paper, Autocomplete, Divider, Link} from '@mui/material'
import {DatePicker, DateTimePicker} from '@mui/x-date-pickers'
import {isString} from 'lodash'
import {Moment} from 'moment'
import React from 'react'
import ReactQuill from 'react-quill'

import {formatDateTime} from '../../utils'
import {stripHtmlTags} from '../../utils/strings'
import {
  AnalysisDescriptionTooltip,
  AnalysisPrivacyStatusSwitch,
  RcfaUserSearch,
  SearchStoppageCode
} from '../common'

import {useCreateAnalysisForm} from './useCreateAnalysisForm'
import {RcfaCreatePlantOption} from './useRcfaCreatePlantOptions'

export interface NavigateToPlantOptions {
  preservedDescription: string
  searchParams: URLSearchParams
}

export interface CreateAnalysisFormProperties {
  onGoBack: () => void
  onGoNext: () => void
  onAnalysisCreated: (analysis: AnalysisInfo) => void
  onGoToStoppageList?: () => Promise<void> | void
  onNavigateToPlant?: (
    plant: RcfaCreatePlantOption,
    options: NavigateToPlantOptions
  ) => Promise<void> | void
  selectedStoppage?: StoppageInfo
  analysisType?: AnalysisType
  canReassign: boolean
}

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{list: 'ordered'}, {list: 'bullet'}, {indent: '-1'}, {indent: '+1'}],
    ['clean']
  ]
}

const getTextLength = (description: string | undefined) =>
  description?.length ? stripHtmlTags(description, []).length : 0

export const CreateAnalysisForm: React.FC<CreateAnalysisFormProperties> = (props) => {
  const {onGoBack, selectedStoppage, canReassign} = props
  const {
    t,
    item,
    setItem,
    customStoppage,
    setCustomStoppage,
    validationError,
    getErrorText,
    fieldsDisabled,
    externalStoppage,
    sOption,
    setSOption,
    stoppagesOptions,
    isRoutePlantAuthorized,
    isLoadingStoppages,
    isPlantScopedInputDisabled,
    selectedPlantOption,
    plantOptions,
    isLoadingPlantOptions,
    navigateToPlant,
    selectStoppage,
    goToStoppageList,
    doSubmit,
    displayStoppageSelect,
    setOwner,
    isLoading
  } = useCreateAnalysisForm(props)

  if (!isLoadingPlantOptions && plantOptions.length === 0) {
    return (
      <Paper
        id="create-analysis-form"
        sx={{
          width: 490,
          height: 'fit-content',
          padding: 3
        }}
      >
        <Stack spacing={2.5}>
          <Typography variant={'h3'} sx={{fontSize: 18, flexGrow: 1, overflow: 'hidden', mb: 1}}>
            {t('cards.defineDetails')}
          </Typography>
          <Typography>{t('errors.noPlantsAvailableForRcfaCreation')}</Typography>
          <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
            <Button
              variant="secondary"
              iconPrefix={<Icon path={mdiArrowLeft} />}
              onClick={onGoBack}
              data-test-id="create-analysis-cancel-button"
            >
              {t('buttons.back')}
            </Button>
          </Box>
        </Stack>
      </Paper>
    )
  }

  return (
    <Paper
      id="create-analysis-form"
      sx={{
        width: 490,
        height: 'fit-content',
        padding: 3
      }}
    >
      <Stack spacing={2.5}>
        <Typography variant={'h3'} sx={{fontSize: 18, flexGrow: 1, overflow: 'hidden', mb: 1}}>
          {t('cards.defineDetails')}
        </Typography>
        <ControlledSelect
          data-cy="selected-plant-input"
          name="selected-plant"
          label={t('label.plant')}
          value={selectedPlantOption?.plantUpmId}
          values={plantOptions.map((option) => ({
            value: option.plantUpmId,
            label: option.plantName || option.plantUpmId
          }))}
          isDisabled={Boolean(selectedStoppage || externalStoppage || isLoadingPlantOptions)}
          onChange={(value) => {
            const plant = plantOptions.find((option) => option.plantUpmId === value)
            if (plant) void navigateToPlant(plant)
          }}
        />
        <Autocomplete
          key={'stoppage'}
          id="stoppage"
          groupBy={(option) => (option.isStoppage ? 'Suggested' : '')}
          options={stoppagesOptions}
          onChange={(_, stoppage) => {
            setSOption(stoppage)
            selectStoppage(stoppage?.id)
          }}
          value={sOption}
          data-test-id={'stoppage-selector'}
          loading={isRoutePlantAuthorized && isLoadingStoppages}
          disabled={isPlantScopedInputDisabled}
          sx={{
            marginBottom: 2,
            maxWidth: 450
          }}
          renderOption={(props, option) => {
            return (
              <Box component={'li'} {...props}>
                <Stack width={'100%'}>
                  <Stack direction={'row'} gap={1} p={1} pl={0}>
                    {option.icon ?? <></>}
                    {option.label}
                  </Stack>
                  {option.id !== 'custom_stoppage' && <Divider />}
                </Stack>
              </Box>
            )
          }}
          renderInput={(params) => (
            <TextField {...params} label={'Stoppage *'} placeholder={'Select stoppage'} />
          )}
          PaperComponent={({children}) => {
            return (
              <Paper>
                {children}
                <Box
                  p={2}
                  textAlign={'center'}
                  onMouseDown={async (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    await goToStoppageList()
                  }}
                >
                  <Link
                    href={'#'}
                    onMouseDown={async (e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      await goToStoppageList()
                    }}
                  >
                    Go to stoppage list
                  </Link>
                </Box>
              </Paper>
            )
          }}
        />

        <TextField
          data-test-id="node-form-title"
          error={validationError.has('title')}
          fullWidth={true}
          helperText={getErrorText('title')}
          label={t('label.analysisTitle')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setItem((nextItem) => ({...nextItem, title: event.target.value}))
          }
          value={item.title}
          defaultValue={externalStoppage?.title}
          required
          disabled={fieldsDisabled}
        />
        <Autocomplete
          key={'analysis-category'}
          id="analysis-category"
          options={analysisCategories}
          onChange={(_, category) =>
            setItem((nextItem) => ({
              ...nextItem,
              category: category?.id || category
            }))
          }
          sx={{
            marginBottom: 2,
            maxWidth: 450
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('label.searchAnalysisCategoryLabel')}
              placeholder={t('label.searchAnalysisCategoryPlaceholder')}
            />
          )}
          disabled={fieldsDisabled}
        />
        <Autocomplete
          key={'analysis-areaType'}
          id="analysis-areaType"
          options={analysisAreaTypes}
          onChange={(_, category) =>
            setItem((nextItem) => ({
              ...nextItem,
              areaType: isString(category) ? category : category ? category.id : category
            }))
          }
          sx={{
            marginBottom: 2,
            maxWidth: 450
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('label.searchAnalysisAreaTypeLabel')}
              placeholder={t('label.searchAnalysisAreaTypePlaceholder')}
            />
          )}
          disabled={fieldsDisabled}
        />
        {displayStoppageSelect && (
          <>
            <SearchStoppageCode
              value={customStoppage}
              onChange={(stoppage) => setCustomStoppage(stoppage)}
            />
            <TextField
              id="equipment"
              data-test-id="stoppage-equipment-input"
              label={t('analysis.new.equipment')}
              onChange={(event) => {
                setItem((nextItem) => ({
                  ...nextItem,
                  equipment: event.target.value
                }))
              }}
            />
            <Stack direction="row" spacing={2}>
              <DateTimePicker
                inputFormat="DD.MM.YYYY HH:mm"
                ampm={false}
                data-test-id="stoppage-start-input"
                label={t('analysis.new.stoppageStart')}
                onChange={(value: Moment | null) => {
                  setItem((nextItem) => ({
                    ...nextItem,
                    stoppageStart: value ? value.toISOString() : value
                  }))
                }}
                value={item.stoppageStart}
                disableFuture
                renderInput={(params) => <TextField variant="filled" size="small" {...params} />}
              />
              <DateTimePicker
                inputFormat="DD.MM.YYYY HH:mm"
                ampm={false}
                data-test-id="stoppage-end-input"
                label={t('analysis.new.stoppageEnd')}
                onChange={(value: Moment | null) => {
                  setItem((nextItem) => ({
                    ...nextItem,
                    stoppageEnd: value ? value.toISOString() : value
                  }))
                }}
                value={item.stoppageEnd}
                disableFuture
                renderInput={(params) => <TextField variant="filled" size="small" {...params} />}
              />
            </Stack>
          </>
        )}
        {externalStoppage && (
          <Stack spacing={2} sx={{pl: 2}}>
            <StoppageSection
              label={t('analysis.new.equipment')}
              value={`${externalStoppage?.equipment?.text}`}
              valueProps={{sx: {textTransform: 'none'}}}
              captionProps={{sx: {textTransform: 'capitalize'}}}
            />
            <StoppageSection
              label={t('stoppage.detail.stoppageCode')}
              value={`${externalStoppage?.stoppageCode?.code} - ${externalStoppage?.stoppageCode?.text}`}
              valueProps={{sx: {textTransform: 'none'}}}
              captionProps={{sx: {textTransform: 'capitalize'}}}
            />
            <StoppageSection
              label={t('stoppage.detail.time')}
              value={`${externalStoppage.start ? formatDateTime(externalStoppage.start) : ''} - ${
                externalStoppage.end ? formatDateTime(externalStoppage.end) : ''
              }`}
              valueProps={{sx: {textTransform: 'none'}}}
              captionProps={{sx: {textTransform: 'capitalize'}}}
            />
          </Stack>
        )}
        <DatePicker
          value={item.dueDate}
          label={'Due Date'}
          inputFormat="DD.MM.YYYY"
          views={['day']}
          disablePast
          onChange={(newDueDate) =>
            newDueDate && setItem((nextItem) => ({...nextItem, dueDate: newDueDate}))
          }
          disabled={fieldsDisabled}
          renderInput={(params) => (
            <TextField
              {...params}
              helperText={validationError.get('dueDate') && t('task.dueDateIsInThePast')}
              sx={{
                width: '100%',
                marginBottom: validationError.get('dueDate') ? 3 : 2
              }}
            />
          )}
        />
        <Box mb={3} style={{display: 'flex', flexDirection: 'column'}}>
          <AnalysisDescriptionTooltip top={30} left={415} />
          <ReactQuill
            modules={modules}
            theme={'snow'}
            onChange={(next) => setItem((nextItem) => ({...nextItem, description: next}))}
            placeholder={`${t('label.description')} *`}
            formats={['bold', 'italic', 'underline', 'list', 'bullet', 'indent']}
            readOnly={fieldsDisabled}
            value={item.description}
          />
          {validationError.has('description') && (
            <Box ml={1} sx={{float: 'left'}}>
              <Typography sx={(theme) => ({color: theme.palette.error.light, fontSize: 12})}>
                {getErrorText('description')}
              </Typography>
            </Box>
          )}
          <Box sx={(theme) => ({color: theme.palette.grey['300'], alignSelf: 'flex-end'})}>
            {getTextLength(item.description)}/{5000}
          </Box>
        </Box>
        {canReassign && (
          <RcfaUserSearch
            assignee={item.owner ?? null}
            label={t('analysis.changeOwner.owner')}
            onChange={setOwner}
            legacyDesign
            disabled={fieldsDisabled}
          />
        )}
        <AnalysisPrivacyStatusSwitch
          value={item.privacyStatus}
          onChange={(privacyStatus) => setItem((nextItem) => ({...nextItem, privacyStatus}))}
        />
        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
          <Button
            variant="secondary"
            iconPrefix={<Icon path={mdiArrowLeft} />}
            onClick={onGoBack}
            data-test-id="create-analysis-cancel-button"
          >
            {t('buttons.back')}
          </Button>
          <Button
            variant="primary"
            disabled={isLoading || isPlantScopedInputDisabled}
            iconSuffix={<Icon path={mdiArrowRight} />}
            onClick={doSubmit}
            data-test-id="create-analysis-save-button"
            id="create-analysis-save-button"
          >
            {isLoading ? <Icon path={mdiLoading} spin /> : t('buttons.startAnalysis')}
          </Button>
        </Box>
      </Stack>
    </Paper>
  )
}
